import React, { useState } from 'react';
import { FiMoreHorizontal, FiEdit3, FiFolder, FiCheckCircle } from 'react-icons/fi';
import { SlNotebook } from 'react-icons/sl';
import dragAndDrop from '@/assets/images/icons/drag-and-drop.svg';
import { useNavigate } from 'react-router-dom';
import { confirm } from '@/lib/confirm';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { t } from 'i18next';
import { useFormik } from 'formik';
import moment from 'moment';
import { ModalWarning } from '@/components/custom/modal/modal-warning';
import usePermission from '@/hooks/usePermission';
import { SubjectCard, SubjectCardBody, SubjectCardBodyHeader, SubjectCardHeader, SubjectCardTextSchedule, SubjectCardTextScheduleWrapper, SubjectCardTitle, SubjectCardVideo } from '@/components/custom/subject';
import { FormSwitch } from '@/components/custom/form';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { BadgeTruncate } from '@/components/ui/badge';
import { CardKpi, CardKpiGrid } from '@/components/ui/card';

export function CourseManageCardLesson({ lesson, currentModuleId, selectedDisciplineId, setLessons, courseId, coursePublished, modulePublished }) {
  const navigate = useNavigate();
  const client = useClient();
  const [requiredLesson, setRequiredLesson] = useState(lesson?.unrequired);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const now = new Date();

  const lessonForm = useFormik({
    initialValues: {
      published: lesson?.published ?? false
    }
  });

  const deleteLesson = async () => {
    try {
      await client.delete(`/learning_system_items/${lesson.id}`);
      toast.success('Aula excluída com sucesso!');
      setLessons(prevState => prevState.filter(item => item.id !== lesson.id));
    } catch (e) {
      toast.error('Erro ao deletar aula');
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: t('projects.warningDeleteLesson'),
      description: t('projects.warningDeleteCannotBeUndoneText'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });
    if (confirmed) {
      deleteLesson();
    }
  };

  const updateLesson = async () => {
    const { error } = await client.mutate(['/learning_system_items', lesson.id], {
      unrequired: !requiredLesson
    });
    if (error) {
      setRequiredLesson(requiredLesson);
      toast.error(error.title || 'Erro ao atualizar obrigatoriedade da aula');
    } else {
      if (!requiredLesson === true) {
        toast.success(t('toast.successSetLessonNonMandatory'));
      } else {
        toast.success(t('toast.successSetLessonMandatory'));
      }
    }
  };

  const videoId = () => {
    const lessonUrl = lesson.url;

    if (['youtube.com', 'youtu.be'].some(e => lessonUrl.includes(e))) {
      const match = lessonUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v?=|embed\/|v\/|live\/|.+\?v=))((\w|-){11})/);
      if (match && match[1]) {
        const id = match[1];
        return { id, type: 'youtube' };
      }
    }
    if (lessonUrl.includes('vimeo.com')) {
      const urlParts = lessonUrl.split('/');
      const id = urlParts[urlParts.length - 1];
      return { id, type: 'vimeo' };
    }
    if (['genially.com', 'genial.ly'].some(e => lessonUrl.includes(e))) {
      const id = lessonUrl.match(/(?:view\.genially\.com\/|view\.genial\.ly\/)([a-zA-Z0-9]+)/)[1];
      return { id, type: 'genially' };
    }
  };

  const videoInfo = videoId();

  const handleUpdateScheduleLesson = async () => {
    const previousPublished = lessonForm.values.published;
    lessonForm.setFieldValue('published', !previousPublished);

    const { error } = await client.mutate(['/learning_system_items', lesson.id], {
      published: !previousPublished
    });

    if (error) {
      lessonForm.setFieldValue('published', previousPublished);
      toast.error(error.title || 'Erro ao publicar aula');
      return;
    }

    toast.success(!previousPublished ? 'Aula publicada com sucesso!' : 'Aula despublicada com sucesso!');
  };

  const handleNavigateToLesson = () => {
    if (!coursePublished && !modulePublished) {
      setIsOpenModal(true);
    } else {
      navigate(`/show-course/${currentModuleId}/classroom/${lesson?.id}`);
    }
  };

  const handlePublishCourseAndModule = async () => {
    setIsSubmitting(true);
    try {
      await client.mutate(`courses/${courseId}`, {
        published: true
      });
      await client.mutate(['learning_systems', currentModuleId], {
        publish: true
      });

      toast.success('Curso e módulo publicado com sucesso!');
      navigate(`/show-course/${currentModuleId}/classroom/${lesson?.id}`);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao publicar');
      setIsOpenModal(false);
      setIsSubmitting(false);
    }
  };

  const hasPermissionToPublish = usePermission('Gerenciar conteúdo', 'Cursos');

  const splittedDate = lesson?.['published_at']?.split(':');

  if (splittedDate?.[1] < 59) {
    splittedDate[1] = Number(splittedDate[1]) + 1;
  }
  const parsedDate = splittedDate?.join(':');

  return (
    <SubjectCard>
      <SubjectCardHeader>
        <img
          src={dragAndDrop}
          alt="ícone de arrastar"
          className="w-3 cursor-grab"
        />
        <SubjectCardVideo>
          {videoInfo && videoInfo.type === 'youtube' && <iframe src={`https://www.youtube.com/embed/${videoInfo.id}`} />}
          {videoInfo && videoInfo.type === 'vimeo' && <iframe src={`https://player.vimeo.com/video/${videoInfo.id}`} />}
          {videoInfo && videoInfo.type === 'genially' && <iframe src={`https://view.genially.com/${videoInfo.id}`} />}
        </SubjectCardVideo>
      </SubjectCardHeader>

      <SubjectCardBody>
        <SubjectCardBodyHeader>
          <SubjectCardTitle title={lesson?.body}>{lesson?.body}</SubjectCardTitle>

          <SubjectCardTextScheduleWrapper>
            {!lesson.published && parsedDate && new Date(parsedDate) > now && <SubjectCardTextSchedule>Agendada para {moment(lesson['published_at']).format('DD/MM/YYYY')}</SubjectCardTextSchedule>}

            {hasPermissionToPublish && (
              <FormSwitch
                label="Publicar"
                checked={lessonForm.values.published}
                onCheckedChange={handleUpdateScheduleLesson}
              />
            )}
          </SubjectCardTextScheduleWrapper>

          <DropdownMenu>
            <DropdownMenuTrigger className="shrink-0">
              <FiMoreHorizontal size={16} />
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => navigate(`/lms/criar/aula/${selectedDisciplineId}/nome/${lesson?.id}/${courseId}`, { state: { moduleId: currentModuleId, editLesson: true } })}>{t('button.edit')}</DropdownMenuItem>
              <DropdownMenuItem onSelect={handleNavigateToLesson}>{t('button.view')}</DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => handleDelete()}
                className="text-danger"
              >
                {t('button.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SubjectCardBodyHeader>

        <FormSwitch
          label={t('textsCommon.mandatoryLesson')}
          checked={!requiredLesson}
          onCheckedChange={() => {
            setRequiredLesson(!requiredLesson);
            updateLesson();
          }}
          className="mb-2"
        />

        <BadgeTruncate
          variant="primary-light"
          size="sm"
          items={lesson.taxonomies}
          className="mb-4"
        />

        <CardKpiGrid variant="fluid">
          <CardKpi
            title={lesson?.learning_system_activities_count + ' atividades'}
            aria-label={lesson?.learning_system_activities_count + ' atividades'}
          >
            <FiEdit3 /> {lesson?.learning_system_activities_count}
          </CardKpi>
          <CardKpi
            title={lesson?.learning_system_files?.length + ' materiais'}
            aria-label={lesson?.learning_system_files?.length + ' materiais'}
          >
            <FiFolder /> {lesson?.learning_system_files?.length}
          </CardKpi>
          <CardKpi
            title={lesson?.learning_system_question_books_count + ' exercícios'}
            aria-label={lesson?.learning_system_question_books_count + ' exercícios'}
          >
            <SlNotebook /> {lesson?.learning_system_question_books_count}
          </CardKpi>
          <CardKpi
            title={lesson?.quiz_items?.length + ' perguntas em tempo real'}
            aria-label={lesson?.quiz_items?.length + ' perguntas em tempo real'}
          >
            <FiCheckCircle /> {lesson?.quiz_items?.length}
          </CardKpi>
        </CardKpiGrid>
      </SubjectCardBody>

      <ModalWarning
        show={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        title="A aula não poderá ser visualizada"
        description="O curso e o módulo correspondente a aula ainda não estão publicados. Deseja publicá-los agora?"
        titleSecondButton="Quero publicar o curso e o módulo"
        onSubmitFirstButton={() => setIsOpenModal(false)}
        onSubmitSecondButton={handlePublishCourseAndModule}
        isSubmitting={isSubmitting}
      />
    </SubjectCard>
  );
}
